/**
 * EmptyState — UX §3.20. NO illustration, NO icon (typography + whitespace
 * do the work). Optional Phosphor icon allowed for the agent variant.
 * Serif h2, single short caption, single primary CTA.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLElement> {
  /** Serif title in h2 weight. */
  title: string;
  /** Optional short caption sentence. */
  description?: string;
  /**
   * Optional icon slot. Per UX spec this is normally absent — only use for
   * exceptional cases (the `agent` variant noted in the assignment).
   */
  icon?: React.ReactNode;
  /** Slot for the single primary CTA. Renders as-is. */
  cta?: React.ReactNode;
}

export const EmptyState = React.forwardRef<HTMLElement, EmptyStateProps>(
  function EmptyState({ title, description, icon, cta, className, ...rest }, ref) {
    return (
      <section
        ref={ref as React.Ref<HTMLElement>}
        className={cn(
          'mx-auto max-w-md text-center py-20 md:py-24 px-6',
          className,
        )}
        {...rest}
      >
        {icon ? (
          <div
            aria-hidden
            className="mb-6 flex justify-center text-ink-500 [&_svg]:h-8 [&_svg]:w-8"
          >
            {icon}
          </div>
        ) : null}
        <h2 className="font-serif text-3xl text-ink-900 leading-tight">
          {title}
        </h2>
        {description ? (
          <p className="mt-4 font-sans text-sm text-ink-500 leading-relaxed">
            {description}
          </p>
        ) : null}
        {cta ? <div className="mt-10">{cta}</div> : null}
      </section>
    );
  },
);
